import { ChevronDownIcon, DotsVerticalIcon, DropdownMenu, IconSize } from '@protoss/ui';
import { Cross2Icon } from '@radix-ui/react-icons';
import * as React from 'react';
import { ResizableDivider } from '../../../components/ResizableDivider';

export type SplitDocTab =
  | 'scope'
  | 'questionnaire'
  | 'systems-landscape'
  | 'estimate'
  | 'contract'
  | 'resource-plan'
  | 'sow'
  | 'docs';

/** A questionnaire section and its groups, for the go-to navigator (three-dot menu). */
export interface SplitNavSection {
  sectionKey: string;
  label: string;
  groups: Array<{ groupKey: string; label: string }>;
}

export interface SplitPaneLayoutProps {
  questionnaireSurface: React.ReactNode;
  leftDocumentSurface: React.ReactNode | null;
  rightDocumentSurface: React.ReactNode | null;
  leftTab: SplitDocTab;
  rightTab: SplitDocTab;
  allTabs: { value: SplitDocTab; label: string }[];
  onCloseLeft: () => void;
  onCloseRight: () => void;
  onChangeLeftTab: (tab: SplitDocTab) => void;
  onChangeRightTab: (tab: SplitDocTab) => void;
  /** Sections (with their groups) for the questionnaire pane's go-to navigator. */
  sections?: SplitNavSection[];
  /** Actions rendered in the questionnaire pane header (e.g. a single save-all button). */
  questionnaireHeaderActions?: React.ReactNode;
}

function TabSwitcher({
  currentTab,
  excludeTab,
  allTabs,
  onChangeTab,
}: {
  currentTab: SplitDocTab;
  excludeTab: SplitDocTab;
  allTabs: { value: SplitDocTab; label: string }[];
  onChangeTab: (tab: SplitDocTab) => void;
}) {
  const label = allTabs.find((t) => t.value === currentTab)?.label ?? currentTab;
  const options = allTabs.filter((t) => t.value !== currentTab && t.value !== excludeTab);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="deal-split-pane__tab-switch"
          aria-label="Switch pane content"
        >
          <span className="deal-split-pane__tab-switch-label">{label}</span>
          <ChevronDownIcon
            width={IconSize.sm}
            height={IconSize.sm}
            className="deal-split-pane__tab-switch-chevron"
            aria-hidden="true"
          />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="dropdown-content" sideOffset={4} align="start">
          {options.map((tab) => (
            <DropdownMenu.Item
              key={tab.value}
              className="dropdown-item"
              onSelect={() => onChangeTab(tab.value)}
            >
              {tab.label}
            </DropdownMenu.Item>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

/**
 * Go-to navigator for the questionnaire pane: lists every section with its groups indented beneath,
 * and scrolls that anchor into view (not a filter - it never hides content). Capped height with an
 * internal scrollbar so long questionnaires stay navigable.
 */
function SectionNavMenu({
  sections,
  onNavigate,
}: {
  sections: SplitNavSection[];
  onNavigate: (target: { sectionKey: string; groupKey?: string }) => void;
}) {
  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button type="button" className="deal-split-pane__icon-btn" aria-label="Go to section">
          <DotsVerticalIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content
          className="dropdown-content deal-split-pane__nav-menu"
          sideOffset={4}
          align="end"
        >
          {sections.map((section) => (
            <React.Fragment key={section.sectionKey}>
              <DropdownMenu.Item
                className="dropdown-item deal-split-pane__nav-section"
                onSelect={() => onNavigate({ sectionKey: section.sectionKey })}
              >
                {section.label}
              </DropdownMenu.Item>
              {section.groups
                .filter((group) => group.label)
                .map((group) => (
                  <DropdownMenu.Item
                    key={group.groupKey}
                    className="dropdown-item deal-split-pane__nav-group"
                    onSelect={() =>
                      onNavigate({ sectionKey: section.sectionKey, groupKey: group.groupKey })
                    }
                  >
                    {group.label}
                  </DropdownMenu.Item>
                ))}
            </React.Fragment>
          ))}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export function SplitPaneLayout({
  questionnaireSurface,
  leftDocumentSurface,
  rightDocumentSurface,
  leftTab,
  rightTab,
  allTabs,
  onCloseLeft,
  onCloseRight,
  onChangeLeftTab,
  onChangeRightTab,
  sections,
  questionnaireHeaderActions,
}: SplitPaneLayoutProps) {
  const [leftWidthPercent, setLeftWidthPercent] = React.useState(50);
  const containerRef = React.useRef<HTMLDivElement>(null);
  // The questionnaire pane's scroll box - the go-to navigator scrolls anchors within it.
  const questionnaireContentRef = React.useRef<HTMLDivElement>(null);

  const handleResize = React.useCallback((delta: number) => {
    setLeftWidthPercent((prev) => {
      const containerWidth = containerRef.current?.clientWidth || 1000;
      const newPercent = prev + (delta / containerWidth) * 100;
      return Math.max(20, Math.min(80, newPercent));
    });
  }, []);

  // Scroll the chosen section/group anchor into view within the questionnaire pane. Scoped to the
  // pane's ref so a matching data-*-key elsewhere on the page can't be targeted.
  const handleNavigate = React.useCallback(
    (target: { sectionKey: string; groupKey?: string }) => {
      const root = questionnaireContentRef.current;
      if (!root) return;
      const selector = target.groupKey
        ? `[data-group-key="${CSS.escape(target.groupKey)}"]`
        : `[data-section-key="${CSS.escape(target.sectionKey)}"]`;
      root.querySelector(selector)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    },
    []
  );

  const renderPaneHeader = (
    tab: SplitDocTab,
    otherTab: SplitDocTab,
    onClose: () => void,
    onChangeTab: (t: SplitDocTab) => void
  ) => (
    <div className="deal-split-pane__header">
      <TabSwitcher
        currentTab={tab}
        excludeTab={otherTab}
        allTabs={allTabs}
        onChangeTab={onChangeTab}
      />
      {tab === 'questionnaire' && sections && sections.length > 0 ? (
        <SectionNavMenu sections={sections} onNavigate={handleNavigate} />
      ) : null}
      {tab === 'questionnaire' ? questionnaireHeaderActions : null}
      <button
        type="button"
        className="deal-split-pane__icon-btn"
        aria-label="Close pane"
        onClick={onClose}
      >
        <Cross2Icon aria-hidden="true" />
      </button>
    </div>
  );

  const renderPaneContent = (tab: SplitDocTab, docSurface: React.ReactNode | null) => (
    <div
      ref={tab === 'questionnaire' ? questionnaireContentRef : undefined}
      className={`deal-split-pane__content${tab === 'questionnaire' ? ' deal-split-pane__content--questionnaire' : ''}`}
    >
      {tab === 'questionnaire' ? questionnaireSurface : docSurface}
    </div>
  );

  return (
    <div className="deal-split-layout" ref={containerRef}>
      <div className="deal-split-pane" style={{ width: `${leftWidthPercent}%` }}>
        {renderPaneHeader(leftTab, rightTab, onCloseLeft, onChangeLeftTab)}
        {renderPaneContent(leftTab, leftDocumentSurface)}
      </div>

      <ResizableDivider direction="vertical" onResize={handleResize} />

      <div className="deal-split-pane" style={{ width: `${100 - leftWidthPercent}%` }}>
        {renderPaneHeader(rightTab, leftTab, onCloseRight, onChangeRightTab)}
        {renderPaneContent(rightTab, rightDocumentSurface)}
      </div>
    </div>
  );
}
